<!-- Maintain_GameSchedule.vue -->
<template>
  <!-- Header / actions -->
  <v-toolbar flat density="comfortable" class="mt-4">
    <v-toolbar-title>ADMIN Game Schedule</v-toolbar-title>
    <v-spacer />
    <v-btn
      color="secondary"
      variant="tonal"
      prepend-icon="mdi-file-upload"
      @click="loadGoogleSheets_GameScheduleData"
    >
      Upload from Google Sheets
    </v-btn>
    <v-btn color="primary" class="ml-2" prepend-icon="mdi-plus" @click="openCreate">
      Add Game
    </v-btn>
  </v-toolbar>

  <!-- Loading overlay -->
  <v-overlay :model-value="loading" class="align-center justify-center" persistent>
    <div class="d-flex flex-column align-center ga-2">
      <v-progress-circular indeterminate size="40" />
      <div>Loading…</div>
    </div>
  </v-overlay>

  <!-- Filters -->
  <v-container fluid class="pt-2">
    <v-card rounded="lg" elevation="2" class="mb-3">
      <v-card-text>
        <v-row dense class="items-end">
          <v-col cols="12" sm="4" md="4">
            <v-select
              label="GameDate"
              :items="uniqueGameDates"
              v-model="gamedateFilter"
              clearable
              density="comfortable"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" sm="4" md="4">
            <v-select
              label="League"
              :items="uniqueLeagues"
              v-model="leagueFilter"
              clearable
              density="comfortable"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" sm="4" md="4">
            <v-select
              label="GenderAge"
              :items="uniqueGenderAges"
              v-model="genderageFilter"
              clearable
              density="comfortable"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" class="d-flex">
            <v-spacer />
            <v-btn variant="text" @click="clearFilters">Clear filters</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Table -->
    <v-card rounded="lg" elevation="2">
      <v-data-table
        :headers="headers"
        :items="filteredGameSchedule"
        item-key="id"
        density="compact"
        class="elevation-0"
        fixed-header
        height="70vh"
        :items-per-page="-1"
        hide-default-footer
        @click:row="onRowClick"
      >
        <template v-slot:[`item.GameDate`]="{ item }">
          <div class="text-left">{{ formatDateDisplay(item.GameDate) }}</div>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex ga-1">
            <v-btn icon="mdi-pencil" size="small" variant="text" @click.stop="openEdit(item)" />
            <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click.stop="deleteGame(item.id)" />
          </div>
        </template>
        <template #no-data>
          <div class="py-8 text-medium-emphasis">No schedule data available</div>
        </template>
      </v-data-table>
    </v-card>
  </v-container>

  <!-- Add/Edit dialog -->
  <v-dialog v-model="dialog.open" max-width="720" scrollable>
    <v-card>
      <v-card-title class="text-h6">
        {{ dialog.isEdit ? 'Edit' : 'Add' }} Scheduled Game
      </v-card-title>
      <v-card-text>
        <v-form ref="formRef" v-model="valid" @submit.prevent>
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-text-field
                label="Date"
                v-model="form.GameDate"
                :rules="[req]"
                density="comfortable"
                hide-details="auto"
                placeholder="YYYY-MM-DD or mm/dd/yyyy"
              />
            </v-col>
            <v-col cols="6" sm="4">
              <v-text-field
                label="Game #"
                v-model="form.GameNumber"
                :rules="[req]"
                density="comfortable"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="6" sm="4">
              <v-text-field
                label="Time"
                v-model="form.GameTime"
                :rules="[req]"
                density="comfortable"
                hide-details="auto"
                placeholder="e.g. 5:00 PM"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                label="Venue"
                v-model="form.Venue"
                :rules="[req]"
                density="comfortable"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                label="League"
                v-model="form.League"
                density="comfortable"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                label="GenderAge"
                v-model="form.GenderAge"
                density="comfortable"
                hide-details="auto"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                label="Home"
                v-model="form.TeamHome"
                density="comfortable"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Away"
                v-model="form.TeamAway"
                density="comfortable"
                hide-details="auto"
              />
            </v-col>

            <v-col cols="12" sm="3">
              <v-text-field label="REF" v-model="form.REF" density="comfortable" hide-details="auto" />
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field label="AR1" v-model="form.AR1" density="comfortable" hide-details="auto" />
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field label="AR2" v-model="form.AR2" density="comfortable" hide-details="auto" />
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field label="REF4TH" v-model="form.REF4TH" density="comfortable" hide-details="auto" />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="closeDialog">Cancel</v-btn>
        <v-btn color="primary" :loading="saving" @click="saveGame">
          {{ dialog.isEdit ? 'Save Changes' : 'Add Game' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, onMounted } from "vue"
import { getAuth, onAuthStateChanged } from "firebase/auth"
import { addDoc, getDocs, doc, updateDoc, deleteDoc, query, where } from "firebase/firestore"
import { useRouter } from "vue-router"
import { app, db, gamescheduleColRef } from "@/firebase"
import { getFunctions, httpsCallable, httpsCallableFromURL } from 'firebase/functions'

// ---------- state ----------
const router = useRouter()
const loading = ref(true)
const saving  = ref(false)
const valid   = ref(false)
const formRef = ref(null)

const GameSchedule = ref([])

const gamedateFilter  = ref("")
const leagueFilter    = ref("")
const genderageFilter = ref("")

const dialog = ref({ open: false, isEdit: false })
const form = ref(resetForm())

// ---------- table headers ----------
const headers = [
  { title: "Date",       key: "GameDate",   width: 120 },
  { title: "Game #",     key: "GameNumber", width: 90 },
  { title: "Time",       key: "GameTime",   width: 90 },
  { title: "League",     key: "League",     width: 120 },
  { title: "GenderAge",  key: "GenderAge",  width: 120 },
  { title: "Home",       key: "TeamHome",   width: 220 },
  { title: "Away",       key: "TeamAway",   width: 220 },
  { title: "Venue",      key: "Venue",      width: 160 },
  { title: "REF",        key: "REF",        width: 140 },
  { title: "AR1",        key: "AR1",        width: 140 },
  { title: "AR2",        key: "AR2",        width: 140 },
  { title: "REF4TH",     key: "REF4TH",     width: 140 },
  { title: "Actions",    key: "actions",    sortable: false, width: 110 },
]

function waitForAuthReady() {
  const auth = getAuth(app)
  return new Promise((resolve) => {
    const unsub = onAuthStateChanged(auth, (user) => {
      unsub();
      resolve(user);
    });
  });
}
// ---------- lifecycle ----------
onMounted(async () => {
  try {
    testPing();
    await loadGameSchedule()
  } finally {
    loading.value = false
  }
})

// ---------- helpers ----------
const req = (v) => (v !== null && v !== undefined && String(v).trim() !== "") || "Required"

function resetForm() {
  return {
    id: "",
    Organization: "",
    GameDate: "",
    GameNumber: "",
    GameTime: "",
    Venue: "",
    League: "",
    GenderAge: "",
    TeamHome: "",
    TeamAway: "",
    REF: "",
    AR1: "",
    AR2: "",
    REF4TH: "",
  }
}

function formatDateDisplay(v) {
  // Accept Firestore Timestamp, JS Date, or string; show as mm/dd/yyyy when possible
  if (!v) return ""
  try {
    let d = v
    if (typeof v === "object" && typeof v.toDate === "function") d = v.toDate()
    else if (typeof v === "object" && "seconds" in v) d = new Date(v.seconds * 1000)
    else if (typeof v === "string") {
      const p = new Date(v)
      if (!Number.isNaN(p.getTime())) d = p
    }
    if (d instanceof Date && !Number.isNaN(d.getTime())) {
      const mm = String(d.getMonth() + 1).padStart(2, "0")
      const dd = String(d.getDate()).padStart(2, "0")
      const yyyy = String(d.getFullYear())
      return `${mm}/${dd}/${yyyy}`
    }
    return String(v)
  } catch { return String(v) }
}

// ---------- loads ----------
async function loadGameSchedule() {
  const org = getAdminOrgName()
  if (!org) return

  const qBase = query(gamescheduleColRef, where("Organization", "==", org))
  const snap  = await getDocs(qBase)

  const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }))

  // sort: GameDate desc, GameNumber asc
  rows.sort((a, b) => {
    const da = new Date(a.GameDate)
    const db = new Date(b.GameDate)
    if (da > db) return -1
    if (da < db) return 1
    const ga = parseInt(a.GameNumber, 10) || 0
    const gb = parseInt(b.GameNumber, 10) || 0
    return ga - gb
  })

  GameSchedule.value = rows
}

// ---------- filters ----------
const uniqueGameDates  = computed(() => Array.from(new Set(GameSchedule.value.map(i => i.GameDate))).sort())
const uniqueLeagues    = computed(() => Array.from(new Set(GameSchedule.value.map(i => i.League))).sort())
const uniqueGenderAges = computed(() => Array.from(new Set(GameSchedule.value.map(i => i.GenderAge))).sort())

const filteredGameSchedule = computed(() => {
  const gd = (gamedateFilter.value || "").toLowerCase()
  const lg = (leagueFilter.value || "").toLowerCase()
  const ga = (genderageFilter.value || "").toLowerCase()

  return GameSchedule.value.filter(i => {
    const f1 = gd ? String(i.GameDate || "").toLowerCase() === gd : true
    const f2 = lg ? String(i.League   || "").toLowerCase() === lg : true
    const f3 = ga ? String(i.GenderAge|| "").toLowerCase() === ga : true
    return f1 && f2 && f3
  })
})

function clearFilters() {
  gamedateFilter.value  = ""
  leagueFilter.value    = ""
  genderageFilter.value = ""
}

// ---------- dialog controls ----------
function openCreate() {
  dialog.value = { open: true, isEdit: false }
  form.value   = { ...resetForm(), Organization: getAdminOrgName() }
}
function openEdit(row) {
  dialog.value = { open: true, isEdit: true }
  form.value   = { ...resetForm(), ...row }
}
function closeDialog() {
  dialog.value.open = false
}

// For row click (anywhere on the row)
function onRowClick(_event, { item }) {
  openEdit(item)
}

// ---------- actions ----------
async function saveGame() {
  const res = await formRef.value?.validate()
  if (!res?.valid) return

  saving.value = true
  try {
    const payload = { ...form.value }
    if (dialog.value.isEdit && form.value.id) {
      await updateDoc(doc(db, "gameschedule", form.value.id), payload)
    } else {
      const org = getAdminOrgName()
      payload.Organization = org
      await addDoc(gamescheduleColRef, payload)
    }
    await loadGameSchedule()
    closeDialog()
  } catch (e) {
    console.error("saveGame", e)
  } finally {
    saving.value = false
  }
}

async function deleteGame(id) {
  if (!confirm("Delete this game?")) return
  try {
    await deleteDoc(doc(db, "gameschedule", id))
    await loadGameSchedule()
  } catch (e) {
    console.error("deleteGame", e)
  }
}

async function loadGoogleSheets_GameScheduleData() {
  const org = getAdminOrgName();
  console.log("Importing game schedule for org:", org);
  if (!org) return alert("User organization is not defined.")

  try {
    const gfunctions = getFunctions(app, 'us-central1');
    const importFn = httpsCallable(gfunctions, "importGameSchedule");
    const res = await importFn({ organization: org });
    const { added = 0, skipped = 0 } = res.data || {};
    await loadGameSchedule();
    alert(`Game schedule imported. Added: ${added}, Skipped: ${skipped}`);
  } catch (e) {
    console.error("importGameSchedule failed:", e);
    alert(e?.message || "Import failed — see console.");
  }
}

function getAdminOrgName() {
  const org = localStorage.getItem("currentUserOrgName")
  if (!org) console.warn("Organization not found in localStorage")
  return org || ""
}

async function testPing() {
  const auth = getAuth(app);
  const user = await waitForAuthReady();
  if (!user) {
    console.warn("No user signed in");
    return;
  }
  await user.getIdToken(true); // ensure fresh token
  const functions = getFunctions(app, 'us-central1');
  const testPing = httpsCallable(functions, "testPing");
  const res = await testPing({});
  console.log("testPing:", res.data); // { ok: true, uid: "<uid>" }
}
</script>

<style scoped>
.text-left { text-align: left; }
</style>
